import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Eye, MapPin, Bed, Bath, Square, Trash2, Heart, Star, ChevronLeft } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

interface SavedProperty {
  id: string;
  created_at: string;
  properties: {
    id: string;
    title: string;
    description: string;
    address: string;
    monthly_price: number;
    type: string;
    bedrooms: number;
    bathrooms: number;
    area: number;
    views: number;
    occupied: boolean;
    furnishing_status: string;
  };
}

interface PropertyImage {
  id: string;
  url: string;
  storage_path: string | null;
  is_main: boolean;
}

const SavedProperties = () => {
  const [saved, setSaved] = useState<SavedProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [propertyImages, setPropertyImages] = useState<Record<string, string | null>>({});

  useEffect(() => {
    fetchSavedProperties();
  }, []);

  const fetchSavedProperties = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSaved([]);
        setLoading(false);
        return;
      }
      
      const { data, error } = await supabase
        .from("saved_properties")
        .select(`
          id,
          created_at,
          properties (
            id,
            title,
            description,
            address,
            monthly_price,
            type,
            bedrooms,
            bathrooms,
            area,
            views,
            occupied,
            furnishing_status
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
        
      if (error) {
        console.error('Error fetching saved properties:', error);
        setSaved([]);
      } else {
        setSaved(data || []);
        // Fetch images for each property
        if (data && data.length > 0) {
          await fetchPropertyImages(data.map(item => item.properties.id));
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setSaved([]);
    }
    setLoading(false);
  };

  const fetchPropertyImages = async (propertyIds: string[]) => {
    const imagePromises = propertyIds.map(async (propertyId) => {
      try {
        const { data: images, error } = await supabase
          .from('property_images')
          .select('*')
          .eq('property_id', propertyId)
          .order('image_order', { ascending: true });

        if (error || !images || images.length === 0) {
          return { propertyId, imageUrl: null };
        }

        // البحث عن أول صورة لها storage_path صالح
        let mainImageData = images.find(img => img.storage_path && img.storage_path.trim() !== '');
        
        // إذا لم توجد صورة بـ storage_path، ابحث عن الصورة الرئيسية
        if (!mainImageData) {
          mainImageData = images.find(img => img.is_main);
        }
        
        // إذا لم توجد صورة رئيسية، استخدم أول صورة
        if (!mainImageData) {
          mainImageData = images[0];
        }

        let imageUrl: string | null = null;

        // إذا كان هناك storage_path صالح، استخدمه للحصول على الرابط
        if (mainImageData.storage_path && mainImageData.storage_path.trim() !== '') {
          const { data } = supabase.storage
            .from('property-images')
            .getPublicUrl(mainImageData.storage_path);
          
          imageUrl = data.publicUrl;
        } 
        // إذا كان هناك URL مباشر وليس blob، استخدمه
        else if (mainImageData.url && !mainImageData.url.startsWith('blob:')) {
          imageUrl = mainImageData.url;
        }

        return { propertyId, imageUrl };
      } catch (error) {
        console.error('Error fetching image for property:', propertyId, error);
        return { propertyId, imageUrl: null };
      }
    });

    const imageResults = await Promise.all(imagePromises);
    const imageMap: Record<string, string | null> = {};
    imageResults.forEach(({ propertyId, imageUrl }) => {
      imageMap[propertyId] = imageUrl;
    });
    setPropertyImages(imageMap);
  };

  const handleRemove = async (savedPropertyId: string) => {
    if (!window.confirm("هل تريد إزالة العقار من المفضلة؟")) return;
    
    try {
      const { error } = await supabase
        .from("saved_properties")
        .delete()
        .eq("id", savedPropertyId);

      if (!error) {
        setSaved(prev => prev.filter(s => s.id !== savedPropertyId));
      }
    } catch (error) {
      console.error('Error removing property:', error);
    }
  };

  const handleRemoveAll = async () => {
    if (!window.confirm("هل تريد إزالة جميع العقارات المحفوظة؟")) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { error } = await supabase
        .from("saved_properties")
        .delete()
        .eq("user_id", user.id);

      if (!error) {
        setSaved([]);
      }
    } catch (error) {
      console.error('Error removing all properties:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <Skeleton className="h-8 w-32 rounded-lg" />
          <Skeleton className="h-8 w-24 rounded-lg" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((item) => (
            <Card key={item} className="overflow-hidden rounded-2xl shadow-lg">
              <Skeleton className="h-48 w-full" />
              <CardContent className="p-5">
                <div className="space-y-3">
                  <Skeleton className="h-6 w-full rounded" />
                  <Skeleton className="h-4 w-3/4 rounded" />
                  <div className="flex gap-2 mt-4">
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    <Skeleton className="h-10 rounded-lg" />
                    <Skeleton className="h-10 rounded-lg" />
                    <Skeleton className="h-10 rounded-lg" />
                    <Skeleton className="h-10 rounded-lg" />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Skeleton className="h-10 w-4/5 rounded-lg" />
                    <Skeleton className="h-10 w-1/5 rounded-lg" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* شريط العنوان والإجراءات */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Heart className="h-6 w-6 text-rose-500 fill-rose-500" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">العقارات المحفوظة</h1>
          </div>
          <p className="text-gray-600">جميع العقارات التي قمت بحفظها في قائمة المفضلة</p>
        </div>
        
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="bg-gradient-to-r from-gray-50 to-gray-100"
            asChild
          >
            <Link to="/properties">
              <ChevronLeft className="h-4 w-4 mr-1" />
              تصفح المزيد
            </Link>
          </Button>
          
          {saved.length > 0 && (
            <Button 
              variant="destructive" 
              onClick={handleRemoveAll}
              className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              حذف الكل
            </Button>
          )}
        </div>
      </div>
      
      {saved.length === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-white to-rose-50 rounded-2xl shadow-lg border border-rose-100">
          <div className="mb-6 flex justify-center">
            <div className="bg-rose-100 w-24 h-24 rounded-full flex items-center justify-center">
              <Heart className="h-12 w-12 text-rose-500 fill-rose-500" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">لا توجد عقارات محفوظة</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            يمكنك حفظ العقارات التي تعجبك بالنقر على أيقونة القلب في صفحة العقار، وسيظهر هنا
          </p>
          <Link to="/properties">
            <Button className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white">
              تصفح العقارات المتاحة
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 text-blue-500 fill-blue-500" />
              <span className="font-medium text-blue-800">
                لديك <span className="font-bold">{saved.length}</span> عقار في قائمة المفضلة
              </span>
            </div>
            <div className="text-sm text-gray-600">
              تم تحديث القائمة الآن
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {saved.map((item) => {
              const property = item.properties;
              if (!property) return null;
              
              const mainImage = propertyImages[property.id];
              
              return (
                <Card 
                  key={item.id} 
                  className="overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 group"
                >
                  {/* صورة العقار الرئيسية */}
                  <div className="relative">
                    <div className="h-52 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
                      {mainImage ? (
                        <img
                          src={mainImage}
                          alt={property.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          onError={(e) => {
                            console.error('Image failed to load:', mainImage);
                          }}
                        />
                      ) : (
                        <div className="text-center text-gray-400 p-4">
                          <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-2">
                            <Square className="h-8 w-8" />
                          </div>
                          <p className="text-sm">صورة العقار غير متوفرة</p>
                        </div>
                      )}
                      
                      {/* طبقة تدرج فوق الصورة */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                    
                    {/* شارة الحالة */}
                    <div className="absolute top-3 left-3">
                      <Badge 
                        className={`font-medium py-1.5 px-3 rounded-full ${
                          property.occupied 
                            ? 'bg-rose-100 text-rose-800' 
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {property.occupied ? "مؤجر" : "متاح"}
                      </Badge>
                    </div>

                    {/* أيقونة القلب للدلالة على أنه محفوظ */}
                    <div className="absolute top-3 right-3">
                      <div className="bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-md">
                        <Heart className="h-5 w-5 fill-rose-500 text-rose-500" />
                      </div>
                    </div>
                    
                    {/* السعر */}
                    <div className="absolute bottom-3 left-3 text-white">
                      <div className="text-xl font-bold">{property.monthly_price.toLocaleString('ar')} ر.ع</div>
                      <div className="text-xs font-light">شهرياً</div>
                    </div>
                  </div>

                  <CardContent className="p-5">
                    {/* العنوان والعنوان الفرعي */}
                    <div className="mb-4">
                      <h3 className="font-bold text-lg text-gray-900 text-right group-hover:text-blue-600 transition-colors line-clamp-1">
                        {property.title}
                      </h3>
                      <div className="flex items-start gap-1 text-gray-600 text-sm mt-1">
                        <MapPin className="h-4 w-4 mt-0.5 text-blue-500" />
                        <span className="text-right line-clamp-2">{property.address}</span>
                      </div>
                    </div>

                    {/* النوع وحالة الأثاث */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge className="py-1 px-3 rounded-full bg-blue-50 text-blue-800 border border-blue-100">
                        {property.type}
                      </Badge>
                      <Badge className="py-1 px-3 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                        {property.furnishing_status === 'furnished' ? 'مفروش' : 
                         property.furnishing_status === 'semi_furnished' ? 'مفروش جزئياً' : 'غير مفروش'}
                      </Badge>
                    </div>

                    {/* المواصفات الأساسية */}
                    <div className="grid grid-cols-4 gap-2 mb-5">
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-blue-50 p-2 rounded-lg mb-1 border border-blue-100">
                          <Bed className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-800">{property.bedrooms}</span>
                        <span className="text-xs text-gray-500">غرف</span>
                      </div>
                      
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-teal-50 p-2 rounded-lg mb-1 border border-teal-100">
                          <Bath className="h-4 w-4 text-teal-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-800">{property.bathrooms}</span>
                        <span className="text-xs text-gray-500">حمام</span>
                      </div>
                      
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-amber-50 p-2 rounded-lg mb-1 border border-amber-100">
                          <Square className="h-4 w-4 text-amber-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-800">{property.area}</span>
                        <span className="text-xs text-gray-500">م²</span>
                      </div>
                      
                      <div className="flex flex-col items-center text-center">
                        <div className="bg-purple-50 p-2 rounded-lg mb-1 border border-purple-100">
                          <Eye className="h-4 w-4 text-purple-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-800">{property.views || 0}</span>
                        <span className="text-xs text-gray-500">مشاهدات</span>
                      </div>
                    </div>

                    {/* الأزرار */}
                    <div className="flex gap-2">
                      <Link to={`/property/${property.id}`} className="flex-1">
                        <Button 
                          variant="outline" 
                          className="w-full bg-gradient-to-r from-white to-gray-50 border border-gray-200 group-hover:border-blue-300"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          عرض التفاصيل
                        </Button>
                      </Link>
                      <Button 
                        variant="destructive" 
                        onClick={() => handleRemove(item.id)}
                        className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedProperties;